/**
 * ADR-0134: pure helpers for the User connections surfaces (consent page,
 * Login redirect honoring, admin settings gating). Kept in lib/ so they are
 * unit-testable without importing the React route files (console convention).
 */

/**
 * ADR-0160 §4.1. `connections_off` and `not_configured` retired with the
 * blanket-default model — "connections are switched off" and "nobody finished
 * configuring them" were two ways of telling a person the same thing, and
 * neither told them what to do about it. Both collapse into `no_agent_role`.
 */
export type ConnectionBlockedReason = "no_agent_role" | "membership_role";

export type ConsentTenant = {
  tenant_id: string;
  tenant_name: string;
  /** ADR-0160: the role THIS person resolves to — their assignment, or the floor. */
  role_name: string | null;
  connect_allowed: boolean;
  blocked_reason?: ConnectionBlockedReason;
  /**
   * ADR-0190: the data-collection disclosure for THIS workspace, or absent when
   * it captures nothing. Per-workspace because capture is per-tenant — a person
   * choosing between workspaces must see what is true for the one they pick.
   */
  data_notice?: string | null;
  /**
   * ADR-0206: the tool-usage disclosure — served on every workspace option,
   * never capture-gated (owner ruling: disclose always).
   */
  usage_notice?: string | null;
  /**
   * The subject-log disclosure — kept per person, and readable per person by
   * workspace leadership and the person's manager or escalation contact. Every
   * workspace option carries it, never capture-gated. Kept apart from the
   * usage notice because different people read the two records.
   */
  subject_notice?: string | null;
};

/**
 * ADR-0206 §4.3 (erratum E2): the disclosures a workspace option carries, in
 * the order both consent screens render them — the capture notice first, then
 * the usage notice, then the subject-log notice. Both screens call this, so they render the same notices by
 * construction. Null (the platform choice, which is not a workspace) → none.
 */
export function consentNotices(option: ConsentTenant | null): string[] {
  if (!option) return [];
  return [option.data_notice, option.usage_notice, option.subject_notice].filter(
    (n): n is string => typeof n === "string" && n.trim().length > 0
  );
}

/**
 * User-facing copy for why a workspace can't be connected (§5.4 — no jargon).
 *
 * Every branch names the way forward, because a refusal that only says "no" is
 * a dead end for the one person who cannot fix it themselves. The two reasons
 * differ in what the admin must actually do, so they do not share a sentence:
 * `no_agent_role` means nobody has assigned this person a role; `membership_role`
 * means general access exists but does not cover them.
 */
export function blockedReasonCopy(reason: ConnectionBlockedReason | undefined): string {
  switch (reason) {
    case "membership_role":
      return "The general connection access here doesn't cover your role. Ask a workspace admin to assign you a connection role in Users & Teams.";
    case "no_agent_role":
    default:
      return "You don't have a connection role in this workspace yet. Ask a workspace admin to assign you one in Users & Teams.";
  }
}

/**
 * §5.1: a `/connect` redirect (the OAuth consent flow) is honored the moment
 * the session is established, before any surface/destination routing. Returns
 * the redirect when it targets a consent surface, else null.
 *
 * ADR-0152 §4.1: `/device` (the device-code verification page) is the second
 * consent surface — same between-apps moment, same immediate honoring.
 */
export function connectRedirectOf(redirectTo: string | null): string | null {
  if (!redirectTo) return null;
  const isConsentPath = ["/connect", "/device"].some((p) => redirectTo === p || redirectTo.startsWith(`${p}?`));
  return isConsentPath ? redirectTo : null;
}

export const OAUTH_CONNECTION_POLICIES = ["off", "admins_builders", "all_members"] as const;
export type OAuthConnectionPolicy = (typeof OAUTH_CONNECTION_POLICIES)[number];

/**
 * §5.2.1 settings gate: enabling connections requires a role (the pair commits
 * together). Returns whether the (policy, roleId) pair is valid to persist.
 */
export function settingsAreValid(policy: string, roleId: string | null): boolean {
  if (!(OAUTH_CONNECTION_POLICIES as readonly string[]).includes(policy)) return false;
  if (policy !== "off" && !roleId) return false;
  return true;
}
